import { AnnotationTypes, PdfStrokeColor, PdfViewerLayoutModes } from './enums';
import { annotRotation, convertSettingsColorToRgb, getPageContainerRect, textNotesFonts, realClickedPage } from '../common';
import { ICustomTextAreaState, ICustomTextNoteElStyle, ITextNoteFont } from '../interfaces';
import { TextNotesFontTypeStyle } from '../enums';
import { Core, WebViewerInstance } from '@pdftron/webviewer';

export class TextNoteManager {
  private readonly displayMode: any = null;
  private get frameRect(): DOMRect {
    return this.frame.getBoundingClientRect() as DOMRect;
  }
  constructor(
    private instance: WebViewerInstance,
    private frame: HTMLIFrameElement,
    private docViewer: Core.DocumentViewer,
    private annotManager: Core.AnnotationManager
  ) {
    this.displayMode = this.docViewer.getDisplayModeManager().getDisplayMode();
  }

  convertAnnotationToEditableElement(
    e: Core.Annotations.FreeTextAnnotation,
  ): ICustomTextAreaState {
    // when you drag the annotation in pdftron, you can drag it so it falls just outside of the edge of the page
    // in that case, e.X and e.Y are negative values (one or both)
    // because those 'fall off the page', the annotation coords don't fall inside the page in the realClickedPage
    // therefore it's 'not on the page', therefore it's not added
    // to fix this, make sure it is not negative!
    const pagePoint = {
      x: Math.max(e.X, 0),
      y: Math.max(e.Y, 0)
    };
    const clickedPage = e.PageNumber;
    const windowPoint = this.displayMode.pageToWindow(pagePoint, clickedPage);
    const scrollElement = this.docViewer.getScrollViewElement();
    const scrollLeft = scrollElement.scrollLeft || 0;
    const scrollTop = scrollElement.scrollTop || 0;
    const zoom = this.docViewer.getPageZoom(clickedPage);
    const style = this.getStyleFromAnnotation(e);
    const data: ICustomTextAreaState = {
      text: e.getContents(),
      style,
      x: windowPoint.x - scrollLeft + this.frameRect.left,
      y: windowPoint.y - scrollTop + this.frameRect.top,
      width: e.Width,
      height: e.Height,
      restrictions: null,
      pageIdx: clickedPage,
      zoom,
      annotType: this.annotationType(e) || null
    };
    data.restrictions = this.calcualteTextNoteRestictions(data);
    this.annotManager.deleteAnnotation(e, { imported: false, force: true });
    return data;
  }

  openNewTextNoteByClick(
    e: MouseEvent,
    customcoords: boolean = false,
    fontSize: number,
    annotType: AnnotationTypes | null,
  ): ICustomTextAreaState | void {
    const realclickedPageIdx = realClickedPage(e.pageX, e.pageY, this.instance, this.frame);
    if (realclickedPageIdx === null) {
      return;
    }

    // original logic:
    const zoom = this.docViewer.getPageZoom(realclickedPageIdx);
    let dataX = customcoords ? e.pageX : this.frameRect.left + e.pageX;
    let dataY = customcoords ? (e.pageY - fontSize) : ((this.frameRect.top + e.pageY) - (fontSize * zoom));

    // 30 JAN 2025
    // added logic to detect notes falling off the pgae
    // get the rect of the page
    const pageRect = getPageContainerRect(realclickedPageIdx, this.frame);
    // if no pageRect, no point in checking anything
    if (pageRect) {
      const fontSizeZoom = fontSize * zoom;
      // first let's check the x-coördinate
      // is it above the page?
      if (pageRect.y > e.pageY - fontSizeZoom) {
        // only substract the fontSizeZoom if it will not be above the page
        dataY = (this.frameRect.top + e.pageY);
      }
      // is it below the page?
      else {
        const pageBottomEdge = this.frameRect.top + pageRect.top + pageRect.height;
        const heightNoteEditor = (fontSizeZoom * 1.25) + 8; // 5 padding, 2 border (1 L&R) && 1.25 is line-height: 125%
        if (pageBottomEdge < (e.pageY + this.frameRect.top) + heightNoteEditor) {
          dataY = pageBottomEdge - heightNoteEditor;
        }
      }
      // second, let's check the y-coördinate
      // is it falling of the right edge?
      const pageRightEdge = pageRect ? (this.frameRect.left + pageRect.left + pageRect.width) : 0;
      const widthNoteEditor = (fontSizeZoom * 3) + 8; // 5 padding, 2 border (1 L&R);
      if (pageRightEdge < this.frameRect.left + e.pageX + widthNoteEditor) {
        dataX = pageRightEdge - widthNoteEditor;
      }
      // no need to check left edge, this won't trigger if next to it
    }
    // END of added logic to detect notes falling off the pgae
    
    const data: ICustomTextAreaState = {
      x: dataX,
      y: dataY,
      pageIdx: realclickedPageIdx,
      restrictions: null,
      zoom,
      width: null,
      height: null,
      style: {
        fontSize
      },
      text: '',
      annotType,
    };
    data.restrictions = this.calcualteTextNoteRestictions(data);
    return data;
  }

  scrollToBeginningOfAnnotation(e: Core.Annotations.FreeTextAnnotation): void {
    const scrollElement = this.docViewer.getScrollViewElement();
    const clickedPage = e.PageNumber;
    const zoom = this.docViewer.getPageZoom(clickedPage);
    let totalHeightOfPreviousPages = 0;

    const pagePoint = {
      x: Math.max(e.X * zoom, 0),
      y: Math.max(e.Y * zoom, 0)
    };

    const isDualPageView = this.instance.UI.getLayoutMode() === PdfViewerLayoutModes.FacingContinuous;

    for (let page = 1; page < clickedPage; page++) {
      let pageHeight = 0;

      if (!isDualPageView) {
        pageHeight = this.docViewer.getPageHeight(page);
      } else if (page + 1 < clickedPage) {
        pageHeight = Math.max(this.docViewer.getPageHeight(page), this.docViewer.getPageHeight(page + 1));
        page++;
      }

      totalHeightOfPreviousPages += pageHeight * zoom;
    }

    if (pagePoint.y < scrollElement.scrollTop - totalHeightOfPreviousPages) {
      // additional 20px to be slightly above
      scrollElement.scrollTo(scrollElement.scrollLeft, totalHeightOfPreviousPages + pagePoint.y - 20);
    }
  }

  async drawTextAnnotation(e: ICustomTextAreaState): Promise<Core.Annotations.Annotation | null> {
    const freeText = await this.createTextNoteAnnotation(e);
    if (!freeText) {
      return null;
    }
    try {
      this.annotManager.addAnnotation(freeText);
      this.annotManager.redrawAnnotation(freeText);
    } catch (err) {
      console.log(err);
    }
    return freeText;
  }

  async rawDeleteAnnotations(annots: Core.Annotations.Annotation[]): Promise<void> {
    this.annotManager.deleteAnnotations(annots, { imported: false, force: true });
  }

  async rawAddAnnotations(annots: Core.Annotations.Annotation[]): Promise<void> {
    this.annotManager.addAnnotations(annots, { imported: false });
    annots.forEach(a => this.annotManager.redrawAnnotation(a));
  }

  private annotationType(e: Core.Annotations.Annotation): AnnotationTypes | null {
    if (e.Subject === AnnotationTypes.StickyNote) {
      return AnnotationTypes.StickyNote;
    } else if (e.Subject === AnnotationTypes.FreeText) {
      return AnnotationTypes.FreeText;
    }

    return null;
  }

  private calcualteTextNoteRestictions(data: ICustomTextAreaState): any | null {
    const page = getPageContainerRect(data.pageIdx, this.frame);
    if (page) {
      const restrictions = page as any;
      restrictions.height = page.height + page.top - data.y + this.frameRect.top;
      restrictions.width = page.left + page.width - data.x + this.frameRect.left;
      if (page.bottom > this.frameRect.bottom) {
        restrictions.height = this.frameRect.height - page.top;
      }
      if (data.y < this.frameRect.top) {
        data.y = this.frameRect.top;
        if (data.style && data.style.fontSize) {
          restrictions.height = restrictions.height - data.style.fontSize;
        }
      }
      if (data.y + restrictions.height > this.frameRect.bottom) {
        restrictions.height = this.frameRect.height - data.y + this.frameRect.top;
      }
      return restrictions as any;
    }
    return null;
  }

  private convertTextAreaCoordsToPageCoords(e: ICustomTextAreaState): any | null {
    // NOTE 8 AUG 2024
    // if x or y is 0, then in common.ts isRectContainsPoint will return null
    // resulting in realclickedPageIdx being null
    // AND I don't know if 0 values are supposed to be filtered out with the if
    // therefore, make sure x and y, here, are never 0!
    const x = Math.max(e.x - this.frameRect.left, 0.1)
    const y = Math.max(e.y - this.frameRect.top, 0.1)
    let realclickedPageIdx = realClickedPage(x, y, this.instance, this.frame);

    // NOTE 13 JAN 2025
    // ICustomTextAreaState should have a pageIdx from the click event when creating the custom text area
    // try using that one
    if (e.pageIdx && !realclickedPageIdx) {
      realclickedPageIdx = e.pageIdx
    } else if (realclickedPageIdx === null) {
      return null;
    }
    e.pageIdx = realclickedPageIdx;
    const scrollElement = this.docViewer.getScrollViewElement();

    const scrollLeft = scrollElement.scrollLeft || 0;
    const scrollTop = scrollElement.scrollTop || 0;

    const coords = {
      x: e.x + scrollLeft - this.frameRect.left,
      y: e.y + scrollTop - this.frameRect.top
    };
    const pageCoords = this.displayMode.windowToPage(coords, realclickedPageIdx);
    return pageCoords;
  }

  private getStyleFromAnnotation(e: Core.Annotations.FreeTextAnnotation): ICustomTextNoteElStyle {
    const style: ICustomTextNoteElStyle = {
      fontSize: Math.round(parseFloat(e.FontSize)),
      fontFamily: this.findFont(e.Font),
      color: (this.findColor(e.TextColor) || PdfStrokeColor.Black),
      fontType: this.detectFontType()
    };
    return style;
  }

  private findFont(fontName: string): ITextNoteFont {
    const result = textNotesFonts.find(i => i.title === fontName);
    if (result) {
      return result;
    }
    return textNotesFonts[0];
  }

  private findColor(color: Core.Annotations.Color): PdfStrokeColor | null {
    let result: PdfStrokeColor | null | string = null;
    for (const key in PdfStrokeColor) {
      if (key) {
        if (color && color.toHexString && PdfStrokeColor[key] !== null && PdfStrokeColor[key] === color.toHexString()) {
          result = PdfStrokeColor[key];
          return result as PdfStrokeColor;
        }
      }
    }
    return PdfStrokeColor.Black;
  }

  private detectFontType(): TextNotesFontTypeStyle {
    return TextNotesFontTypeStyle.NORMAL;
  }

  private async createTextNoteAnnotation(e: ICustomTextAreaState): Promise<Core.Annotations.FreeTextAnnotation | void> {
    let annotation = new this.instance.Core.Annotations.FreeTextAnnotation();
    const pdfCoords = this.convertTextAreaCoordsToPageCoords(e);
    if (!pdfCoords) {
      return;
    }
    const emptyNote = !e.text;
    if (e && e.pageIdx !== null && e.width && e.height) {
      const zoom = this.docViewer.getPageZoom(e.pageIdx);
      annotation.PageNumber = e.pageIdx;
      const doc = await this.docViewer.getDocument().getPDFDoc();
      const page = await doc.getPage(e.pageIdx);
      const rotation = await page.getRotation();

      const w = e.width / zoom;
      const h = e.height / zoom;

      annotation.X = pdfCoords.x;
      annotation.Y = pdfCoords.y;
      annotation.Width = w;
      annotation.Height = h;
      annotation = annotRotation(annotation, rotation) as Core.Annotations.FreeTextAnnotation;

      annotation.setContents(e.text || '');
      let fontzise = '12';
      if (e.style && e.style.fontFamily && e.style.color) {
        annotation.Font = e.style.fontFamily.title;
        annotation.TextColor = convertSettingsColorToRgb(this.instance, e.style.color);
        fontzise = e.style.fontSize + 'pt';
      }
      annotation.FontSize = fontzise;
      annotation.setPadding(new this.instance.Core.Math.Rect(0, 0, 0, 0));
      switch (e.annotType) {
        case AnnotationTypes.FreeText:
          annotation.Subject = AnnotationTypes.FreeText;
          if (emptyNote) {
            annotation.StrokeThickness = 1;
            if (e.style && e.style.color) {
              annotation.StrokeColor = convertSettingsColorToRgb(this.instance, e.style.color);
            }
          } else {
            annotation.StrokeThickness = 0;
          }
          break;
        case AnnotationTypes.StickyNote:
          annotation.Subject = AnnotationTypes.StickyNote;
          annotation.FillColor = convertSettingsColorToRgb(this.instance, PdfStrokeColor.Yellow);
          annotation.StrokeThickness = 1;
          break;
        default:
          annotation.Subject = AnnotationTypes.FreeText;
          annotation.StrokeThickness = 0;
          break;
      }
    }
    return annotation;
  }
}
